<template lang="pug">
	content-page
		div(slot="nav")
			navigation-default

		h1 {{cluster.name}}
		.infosheet.ml-4(style="float:right")
			v-card.mb-6.pt-3(outlined color="info")
				factsheet(v-bind="cluster" hide-map)
		ws-content(:value="cluster.content")
		p(v-if="cluster.url")
			i18n(path="page.clusters.cluster:more.info.at.{url}")
				template(#url)
					a(:href="cluster.url" slot="url" target="_blank" rel="nofollow") {{cluster.url}}

		skipas-price-table(:skipas="cluster.skipas" :destination="cluster.name")

		h2.mt-3.mb-3 {{$t('page.clusters.cluster:areas.in.region.{cluster}', {cluster:cluster.name})}}
		v-data-iterator(:items="cluster.areas" disable-pagination hide-default-footer )
			template(#item="{item:area}")
				area-list-item-head.mb-6(v-bind="area")
</template>

<style lang="scss">
.factsheet {
	background: transparent !important;
}
</style>

<script>
import gql from 'graphql-tag';
import Content from '@dengl/common/components/content.vue';
import SkipasPriceTable from '@/components/skipas-price-table.vue';
import AreaListItemHead from '@/components/area-list-item-head.vue';
import Factsheet from '@/components/areas/factsheet.vue';
import NavigationDefault from '@/components/navigation-default';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import { interpolateFields } from '@/utils/interpolate.js';

const query = gql`
	query getClusterdetails($slug: String!, $preferedCity: String!) {
		cluster(slug: $slug) {
			id
			name
			slug
			content {
				...ContentDataFragment
				introduction(length: 160)
			}
			url
			...MetaFragment
			...SkipasInfo
			areas {
				id
				name
				slug
				skimap: defaultSkimap {
					url
					alt
				}
				country {
					id
					name
					countryCode
				}
				minElevation
				maxElevation
				reviews {
					average
					voteCount
				}

				departures {
					highway {
						distance
					}
					city(city: $preferedCity) {
						distance
						node {
							id
							name
						}
					}
				}
				snowheights(pageInfo: { first: 1 }) {
					nodes {
						snowMountain
						snowValley
					}
				}
				pistes: slopeinfo {
					total
					green_percentage
					blue_percentage
					red_percentage
					black_percentage
					green: slopeGreen
					blue: slopeBlue
					red: slopeRed
					black: slopeBlack
					gondola
					chairlift
					tbar
					magiccarpet
				}
			}
		}
	}
	${SkipasPriceTable.fragments}
	${Content.fragments}
	${MetaFragment}
`;

const calculateTotalSlopes = areas => {
	const slopeinfo = areas.reduce(
		(combined, area) => {
			combined.green += area.pistes.green;
			combined.blue += area.pistes.blue;
			combined.red += area.pistes.red;
			combined.black += area.pistes.black;
			combined.total += area.pistes.total;
			combined.gondola += area.pistes.gondola;
			combined.chairlift += area.pistes.chairlift;
			combined.tbar += area.pistes.tbar;
			combined.magiccarpet += area.pistes.magiccarpet;
			return combined;
		},
		{
			total: 0,
			green: 0,
			blue: 0,
			red: 0,
			black: 0,
			gondola: 0,
			chairlift: 0,
			tbar: 0,
			magiccarpet: 0
		}
	);

	slopeinfo.green_percentage = Math.round(
		(slopeinfo.green / slopeinfo.total) * 100
	);
	slopeinfo.blue_percentage = Math.round(
		(slopeinfo.blue / slopeinfo.total) * 100
	);
	slopeinfo.red_percentage = Math.round(
		(slopeinfo.red / slopeinfo.total) * 100
	);
	slopeinfo.black_percentage = Math.round(
		(slopeinfo.black / slopeinfo.total) * 100
	);

	return slopeinfo;
};

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/skipas/:cluster',
			'de-DE': '/skipas/:cluster',
			'nl-BE': '/skipas/:cluster'
		}
	},
	components: {
		SkipasPriceTable,
		AreaListItemHead,
		Factsheet,
		NavigationDefault
	},
	layout: 'default',
	asyncData({ params, app }) {
		app.ads({});
		return app.$graphClient
			.query({
				query,
				variables: {
					slug: params.cluster,
					preferedCity: app.store.getters.preferedCity
				}
			})
			.then(({ data }) => {
				data.cluster.slopeinfo = calculateTotalSlopes(data.cluster.areas);

				const interpolater = interpolateFields({
					find: '{pistes}',
					replace: data.cluster.slopeinfo.total
				});
				interpolater(data.cluster.meta, ['title']);

				if (!data.cluster.meta.description) {
					data.cluster.meta.description = data.cluster.content.introduction;
				}

				app.meta(data.cluster.meta);
				app.breadcrumbs({
					[app.i18n.t(`page.areas:heading`)]: {
						name: 'areas'
					},
					[data.cluster.name]: {
						name: 'areas-skipasses'
					}
				});

				return data;
			})
			.catch(error => {
				if (error.message === 'GraphQL error: Cluster not found') {
					return app.httpError(404, null, 'clusters-cluster-02');
				}
				return app.httpError(500, null, 'clusters-cluster-01', error);
			});
	},
	data() {
		return {};
	},

	head() {
		return {
			...this.$structuredData()
		};
	}
};
</script>
